import { createSlice } from "@reduxjs/toolkit";
import {
  getHomePosts,
  getPostById,
  createPost,
  updatePost,
  deletePost,
  getMyPosts,
  getAllPostsAdmin,
  getPendingPosts,
  togglePostActive,
  adminDeletePost,
  togglePostTrending,
  getTrendingPosts,
  togglePostSponsored,
} from "./thunk";
import { toggleLike } from "../5-like/thunk";
import { toggleBookmark } from "../4-bookmark/thunk";
import { createComment } from "../6-comment/thunk";

const initialState = {
  posts: [],
  trendingPosts: [],
  myPosts: [],
  post: null,
  loading: false,
  error: null,

  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
  },
};

const postSlice = createSlice({
  name: "posts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ========================
      // GET HOME POSTS
      // ========================
      .addCase(getHomePosts.pending, (state) => {
        state.loading = true;
      });
    // .addCase(getHomePosts.fulfilled, (state, action) => {
    //   state.loading = false;
    //   state.posts = action.payload.data;

    //   if (action.payload.responseDetails?.pagination) {
    //     state.pagination = action.payload.responseDetails.pagination;
    //   }
    // })
    builder
      .addCase(getHomePosts.fulfilled, (state, action) => {
        const { data, responseDetails } = action.payload;

        if (action.meta.arg.page > 1) {
          state.posts = [...state.posts, ...data];
        } else {
          state.posts = data;
        }

        state.pagination = responseDetails.pagination || {};
        state.loading = false;
      })
      .addCase(getHomePosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================
      // GET TRENDING POSTS
      // ========================
      .addCase(getTrendingPosts.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTrendingPosts.fulfilled, (state, action) => {
        const { data, responseDetails } = action.payload;

        if (action.meta.arg.page > 1) {
          state.trendingPosts = [...state.trendingPosts, ...data];
        } else {
          state.trendingPosts = data;
        }

        state.pagination = responseDetails.pagination || {};
        state.loading = false;
      })

      .addCase(getTrendingPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================
      // TOGGLE LIKE (LISTEN FROM HOME POSTS)
      // ========================
      .addCase(toggleLike.fulfilled, (state, action) => {
        const postId = action.meta.arg;
        const { liked } = action.payload;

        const updatePost = (post) => {
          if (post && post.id === postId) {
            post.liked = liked;
            post.likeCount += liked ? 1 : -1;
          }
        };

        state.posts.forEach(updatePost);
        updatePost(state.post);
      })

      // ========================
      // TOGGLE BOOKMARK (LISTEN FROM HOME POSTS)
      // ========================
      .addCase(toggleBookmark.fulfilled, (state, action) => {
        const postId = action.meta.arg;

        const post = state.posts.find((p) => p.id === postId);
        if (post) {
          post.bookmarked = !post.bookmarked;
        }

        if (state.post && state.post.id === postId) {
          state.post.bookmarked = !state.post.bookmarked;
        }
      })
      // ========================
      // GET SINGLE POST
      // ========================
      .addCase(getPostById.pending, (state) => {
        state.loading = true;
        state.post = null;
      })
      .addCase(getPostById.fulfilled, (state, action) => {
        state.loading = false;
        state.post = action.payload;
      })
      .addCase(getPostById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createComment.fulfilled, (state, action) => {
        state.loading = false;

        if (state.post) {
          state.post.Comments = [
            action.payload,
            ...(state.post.Comments || []),
          ];

          state.post.commentsCount = (state.post.commentsCount || 0) + 1;
        }
      })
      // ========================
      // GET MY POSTS
      // ========================
      .addCase(getMyPosts.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMyPosts.fulfilled, (state, action) => {
        state.loading = false;
        state.myPosts = action.payload.data;

        if (action.payload.responseDetails?.pagination) {
          state.pagination = action.payload.responseDetails.pagination;
        }
      })
      .addCase(getMyPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================
      // CREATE POST
      // ========================
      .addCase(createPost.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.loading = false;
        state.myPosts = [action.payload, ...state.myPosts];
        state.pagination.totalCount += 1;
      })
      .addCase(createPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================
      // UPDATE POST
      // ========================
      .addCase(updatePost.fulfilled, (state, action) => {
        const index = state.myPosts.findIndex(
          (p) => p.id === action.payload.id,
        );
        if (index !== -1) {
          state.myPosts[index] = action.payload;
        }
      })

      // ========================
      // DELETE POST
      // ========================
      .addCase(deletePost.fulfilled, (state, action) => {
        state.myPosts = state.myPosts.filter((p) => p.id !== action.payload);
      })

      // ========================
      // ADMIN – GET ALL POSTS
      // ========================
      .addCase(getAllPostsAdmin.fulfilled, (state, action) => {
        state.posts = action.payload.data;
        if (action.payload.responseDetails?.pagination) {
          state.pagination = action.payload.responseDetails.pagination;
        }
      })

      // ========================
      // ADMIN – GET PENDING POSTS
      // ========================
      .addCase(getPendingPosts.fulfilled, (state, action) => {
        state.posts = action.payload.data;
        if (action.payload.responseDetails?.pagination) {
          state.pagination = action.payload.responseDetails.pagination;
        }
      })

      // ========================
      // ADMIN – TOGGLE ACTIVE
      // ========================
      .addCase(togglePostActive.fulfilled, (state, action) => {
        const post = state.posts.find((p) => p.id === action.payload.id);
        if (post) {
          post.isActive = action.payload.isActive;
        }
      })

      // ========================
      // ADMIN – TOGGLE TRENDING
      // ========================
      .addCase(togglePostTrending.fulfilled, (state, action) => {
        const post = state.posts.find((p) => p.id === action.payload.id);
        if (post) {
          post.isTrending = action.payload.isTrending;
        }
      })

      // ========================
      // ADMIN – TOGGLE SPONSERD
      // ========================
      .addCase(togglePostSponsored.fulfilled, (state, action) => {
        const post = state.posts.find((p) => p.id === action.payload.id);
        if (post) {
          post.isSponsored = action.payload.isSponsored;
        }
      })

      // ========================
      // ADMIN – DELETE POST
      // ========================
      .addCase(adminDeletePost.fulfilled, (state, action) => {
        state.posts = state.posts.filter((p) => p.id !== action.payload);
      });
  },
});

export default postSlice.reducer;
